// WorkOrder.jsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import {
  getOrders,
  deleteOrder,
  updateOrders,
  createOrders,
  getERPPartes,getOrderReport
} from "../common/api/orders";
import { getUserByUsername } from "../common/api/users";
import {
  isAuthenticated,
  getCurrentUsername,
  canModifyOrderStates,
} from "../common/api/auth";
import ModalDeleteOrder from "../components/order/ModalDeleteOrder";
import ModalViewReport from "../components/order/ModalViewReport";
import ModalEditAssignment from "../components/order/ModalEditAssignment";
import ModalSelectClient from "../components/user-management/ModalSelectClient";
import ModalMapOrders from "../components/order/ModalMapOrdersMB";
import { useToast } from "../hooks/useToast";
import ModalCreateOrder from "../components/order/ModalCreateOrder";
import ModalBulkDeleteOrder from "../components/order/ModalBulkDeleteOrder";

function WorkOrder() {
  const [showTable, setShowTable] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [clientCodId, setClientCodId] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [technicianNames, setTechnicianNames] = useState({});
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(true);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [visibleCount, setVisibleCount] = useState(0);
  const [groupNames, setGroupNames] = useState({});
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapFocusedOrderId, setMapFocusedOrderId] = useState(null);
  const [ordersToShowInMap, setOrdersToShowInMap] = useState([]);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [erpDataForNewOrders, setErpDataForNewOrders] = useState([]);
  const [loadingCreateOrderData, setLoadingCreateOrderData] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    tipoParte: 180,
    tecnico: 180,
    grupoTecnico: 180,
    estado: 160,
    reportado: 140,
    cups: 220,
    cntId: 180,
    actualizado: 200,
    coordX: 150,
    coordY: 150,
    nombre: 240,
    direccion: 280,
    acciones: 140,
  });
  const columnResizeState = useRef({
    columnKey: null,
    startX: 0,
    startWidth: 0,
  });
  const formatShortValue = (value) => {
    if (!value && value !== 0) {
      return "";
    }

    const stringValue = String(value);

    if (stringValue.length <= 5) {
      return stringValue;
    }

    return `${stringValue.slice(0, 5)}...`;
  };

  const formatCoordinateValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const stringValue = String(value);

    if (!stringValue.includes(".")) {
      return stringValue;
    }

    const [integerPart, ...decimalParts] = stringValue.split(".");
    const decimalPart = decimalParts.join(".");

    if (!decimalPart || decimalPart.length <= 2) {
      return stringValue;
    }

    return `${integerPart}.${decimalPart.slice(0, 2)}...`;
  };
  const isReportado = (value) => String(value ?? "").trim() === "1";

  const formatReportado = (value) => (isReportado(value) ? "Sí" : "No");
  const columnStyleMap = useMemo(() => {
    const styles = {};
    Object.entries(columnWidths).forEach(([key, value]) => {
      styles[key] = { width: value, minWidth: value };
    });
    return styles;
  }, [columnWidths]);

  const handleColumnResizeMouseMove = useCallback((event) => {
    const { columnKey, startX, startWidth } = columnResizeState.current;

    if (!columnKey) {
      return;
    }

    const deltaX = event.clientX - startX;
    const newWidth = Math.max(80, startWidth + deltaX);

    setColumnWidths((prevWidths) => {
      if (prevWidths[columnKey] === newWidth) {
        return prevWidths;
      }

      return { ...prevWidths, [columnKey]: newWidth };
    });
  }, []);

  const handleColumnResizeMouseUp = useCallback(() => {
    if (!columnResizeState.current.columnKey) {
      return;
    }

    columnResizeState.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.classList.remove("column-resizing");
    document.removeEventListener("mousemove", handleColumnResizeMouseMove);
    document.removeEventListener("mouseup", handleColumnResizeMouseUp);
  }, [handleColumnResizeMouseMove]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("column-resizing");
      document.removeEventListener("mousemove", handleColumnResizeMouseMove);
      document.removeEventListener("mouseup", handleColumnResizeMouseUp);
    };
  }, [handleColumnResizeMouseMove, handleColumnResizeMouseUp]);

  const handleColumnResizeMouseDown = (event, columnKey) => {
    event.preventDefault();
    event.stopPropagation();

    const headerCell = event.currentTarget.parentElement;
    const startWidth = headerCell?.offsetWidth || columnWidths[columnKey] || 0;

    columnResizeState.current = {
      columnKey,
      startX: event.clientX,
      startWidth,
    };

    document.body.classList.add("column-resizing");
    document.addEventListener("mousemove", handleColumnResizeMouseMove);
    document.addEventListener("mouseup", handleColumnResizeMouseUp);
  };

  const handleResizeHandleClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  // Filtri per colonna
  const [columnFilters, setColumnFilters] = useState({
    tipoParte: "",
    tecnico: "",
    grupoTecnico: "",
    estado: "",
    reportado: "",
    cups: "",
    cntId: "",
    actualizado: "",
    coordX: "",
    coordY: "",
    nombre: "",
    direccion: "",
  });

  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();
  const DEBUG_ENABLE_REPORT = false;
  const ENABLE_REASSIGNMENT = true;
  const canReassign = ENABLE_REASSIGNMENT; // Può aggiungere controllo ruolo se serve
  const CLIENT_CODE_STORAGE_KEY = "workorders_selected_client_code";
  const CLIENT_NAME_STORAGE_KEY = "workorders_selected_client_name";
  const INITIAL_BATCH_SIZE = 40;
  const BATCH_INCREMENT = 40;
  const FEATURE_UNDER_CONSTRUCTION_MESSAGE =
    "Funcionalidad en construcción. Estamos trabajando para habilitar esta opción próximamente en la plataforma web.";
  // Flag per abilitare/disabilitare modifica stati
  // TODO: cambiare a true quando il backend supporterà pienamente la modifica da Web
  const ENABLE_STATE_MODIFICATION = false;

  // Controlla se l'utente ha i permessi necessari
  const userCanModify = canModifyOrderStates();

  // La modifica è abilitata solo se ENTRAMBE le condizioni sono vere:
  // 1. Feature flag attivato (ENABLE_STATE_MODIFICATION)
  // 2. Utente ha il ruolo corretto (userCanModify)
  const canModify = ENABLE_STATE_MODIFICATION && userCanModify;
  const tipoParteLabels = {
    1: "Cambio contador",
    2: "Lectura s02",
    3: "Lectura s04",
    4: "Lectura s05",
    5: "Lectura s06",
    6: "Prime1.4",
  };

  const estadoLabels = {
    0: "Ordenes nuevas (NotStarted) y En Pausa",
    1: "Nuevo",
    2: "En progreso",
    3: "En Pausa",
    4: "Terminado",
    5: "Cerrado",
  };
  /*
   const resolveTechnicianName = (order) => {
    if (!order?.COD_ID_USER) {
      return "";
    }

    if (technicianNames[order.COD_ID_USER]) {
      return technicianNames[order.COD_ID_USER];
    }

    return String(order.COD_ID_USER);
  };

  const resolveGroupName = (order) => {
    if (!order?.COD_ID_USER_GROUP) {
      return "";
    }

    if (groupNames[order.COD_ID_USER_GROUP]) {
      return groupNames[order.COD_ID_USER_GROUP];
    }

    return String(order.COD_ID_USER_GROUP);
  };

  const mapTechniciansFromList = (list, clientCode) => {
    if (!Array.isArray(list)) return {};

    return list.reduce((acc, technician) => {
      const id = technician?.COD_ID || technician?.cod_id || technician?.ID;
      if (!id) return acc;

      const technicianClient =
        technician?.COD_CLIENTE ??
        technician?.codCliente ??
        technician?.CLIENTE ??
        technician?.ID_CLIENTE;

      if (
        clientCode &&
        technicianClient &&
        String(technicianClient) !== String(clientCode)
      ) {
        return acc;
      }

      const displayName =
        technician?.NOMBRE ??
        technician?.NOMBRE_COMPLETO ??
        technician?.USER ??
        technician?.EMAIL ??
        `ID ${id}`;

      acc[id] = displayName;
      return acc;
    }, {});
  };

  const mapGroupsFromList = (list, clientCode) => {
    if (!Array.isArray(list)) return {};

    return list.reduce((acc, group) => {
      const id =
        group?.COD_ID ||
        group?.cod_id ||
        group?.ID ||
        group?.id ||
        group?.COD_ID_GRUPO;

      if (!id) {
        return acc;
      }

      const groupClient =
        group?.COD_CLIENTE ||
        group?.cod_cliente ||
        group?.CLIENTE ||
        group?.ID_CLIENTE;

      if (
        clientCode &&
        groupClient &&
        String(groupClient) !== String(clientCode)
      ) {
        return acc;
      }

      const displayName =
        group?.NOMBRE ||
        group?.nombre ||
        group?.GRUPO ||
        group?.NAME ||
        `ID ${id}`;

      acc[id] = displayName;
      return acc;
    }, {});
  };

  const fetchGroupsForClient = async (clientCode, usernameParam) => {
    if (!clientCode) {
      return {};
    }

    try {
      const baseGroups = await getGroups(
        isAdminUser ? "" : usernameParam || ""
      );
      const groupMap = mapGroupsFromList(baseGroups, clientCode);
      return groupMap;
    } catch (err) {
      console.error("Error al cargar los grupos:", err);
      return {};
    }
  };

  const fetchTechnicians = async (clientCode) => {
    if (!clientCode) return {};

    try {
      const clientUsers = await getUsersByClient(clientCode);
      const clientMap = mapTechniciansFromList(clientUsers, clientCode);
      if (Object.keys(clientMap).length > 0) {
        return clientMap;
      }
    } catch (err) {
      console.error("Error al cargar los usuarios del cliente:", err);
    }

    try {
      const allUsers = await getUsers();
      const allMap = mapTechniciansFromList(allUsers, clientCode);
      if (Object.keys(allMap).length > 0) {
        return allMap;
      }
    } catch (err) {
      console.error("Error al cargar la lista completa de usuarios:", err);
    }

    return {};
  };
*/
  const resolveOrderClientCode = (order) => {
    if (!order || typeof order !== "object") {
      return "";
    }

    const candidates = [
      order.COD_ID_GROUP,
      order.COD_CLIENTE,
      order.COD_ID_CLIENTE,
      order.COD_CLIENTE_PARTE,
      order.codCliente,
      order.cod_cliente,
      order.cod_id_group,
      order.cod_id_cliente,
      order.CLIENTE,
      order.ID_CLIENTE,
    ];

    for (const candidate of candidates) {
      if (
        candidate !== undefined &&
        candidate !== null &&
        String(candidate).trim() !== ""
      ) {
        return String(candidate);
      }
    }

    return "";
  };

  const filterOrdersByClient = (ordersList, clientCode) => {
    if (!clientCode) {
      return Array.isArray(ordersList) ? ordersList : [];
    }

    const normalizedCode = String(clientCode).trim();
    const source = Array.isArray(ordersList) ? ordersList : [];

    return source.filter((order) => {
      const orderClient = resolveOrderClientCode(order);
      return orderClient && orderClient === normalizedCode;
    });
  };

  const loadOrdersForClient = async (clientCode) => {
    if (!clientCode) {
      setOrders([]);
      setTechnicianNames({});
      setGroupNames({});
      return;
    }

    if (!isAuthenticated()) {
      toast.error("Sesión expirada");
      navigate("/login");
      return;
    }

    const username = getCurrentUsername();
    if (!username) {
      toast.error("No se pudo extraer el usuario del token.");
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const assignParam = isAdminUser ? 3 : 2;
      const stateParam = isAdminUser ? "" : -1;
      const [data, techniciansMap, groupsMap] = await Promise.all([
        getOrders(username, stateParam, assignParam),
        //fetchTechnicians(clientCode),
        //fetchGroupsForClient(clientCode, username),
      ]);

      const filtered = filterOrdersByClient(data, clientCode);
      setOrders(filtered);
      setTechnicianNames(techniciansMap);
      setGroupNames(groupsMap);
      setShowTable(true);
    } catch (err) {
      console.error("Error completo:", err);
      setGroupNames({});
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseClientModal = () => {
    setShowClientModal(false);

    if (!clientCodId) {
      navigate("/home");
    } else {
      setSelectedClient(clientName);
    }
  };

  const handleSelectClient = async () => {
    if (!selectedClient) {
      toast.error("Selecciona un cliente");
      return;
    }

    const client = availableClients.find(
      (c) => c?.NOMBRE_CLIENTE === selectedClient
    );

    if (!client) {
      toast.error("Cliente no encontrado");
      return;
    }

    const clientCode =
      client?.COD_CLIENTE ??
      client?.cod_cliente ??
      client?.COD_ID ??
      client?.ID ??
      "";

    if (!clientCode && clientCode !== 0) {
      toast.error("Cliente sin identificador válido");
      return;
    }

    const normalizedCode = String(clientCode);

    setClientName(client.NOMBRE_CLIENTE || "");
    setClientCodId(normalizedCode);
    setShowClientModal(false);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(CLIENT_CODE_STORAGE_KEY, normalizedCode);
      window.localStorage.setItem(
        CLIENT_NAME_STORAGE_KEY,
        client.NOMBRE_CLIENTE || ""
      );
    }

    await loadOrdersForClient(normalizedCode);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!isAuthenticated()) {
        navigate("/login");
        return;
      }

      const username = getCurrentUsername();
      if (!username) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        const user = await getUserByUsername(username);
        const associatedClients = Array.isArray(user?.CLIENTES_ASOCIADOS)
          ? user.CLIENTES_ASOCIADOS
          : [];
        const adminRole = Array.isArray(user?.ROLES)
          ? user.ROLES.some(
              (role) =>
                typeof role?.Rol === "string" &&
                role.Rol.toUpperCase() === "ADMINISTRADOR"
            )
          : false;
        setIsAdminUser(adminRole);
        let clientsList = associatedClients;

        if (!clientsList.length && user?.COD_CLIENTE) {
          clientsList = [
            {
              COD_CLIENTE: user.COD_CLIENTE,
              NOMBRE_CLIENTE: user.NOMBRE_CLIENTE || "",
            },
          ];
        }

        setAvailableClients(clientsList);

        if (clientsList.length > 0) {
          let defaultClient = clientsList[0];

          if (typeof window !== "undefined") {
            const storedCode = window.localStorage.getItem(
              CLIENT_CODE_STORAGE_KEY
            );

            if (storedCode) {
              const match = clientsList.find(
                (clientItem) =>
                  String(clientItem?.COD_CLIENTE ?? clientItem?.cod_cliente) ===
                  String(storedCode)
              );

              if (match) {
                defaultClient = match;
              }
            }
          }

          setSelectedClient(defaultClient?.NOMBRE_CLIENTE || "");
        } else {
          toast.error("No tienes clientes asociados");
          setShowClientModal(false);
          setShowTable(false);
        }
      } catch (err) {
        console.error("Error completo:", err);
        toast.error("Error al cargar los datos del usuario");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [navigate, toast]);

  useEffect(() => {
    applyColumnFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, columnFilters]);

  const baseOrders = useMemo(
    () => (isFilterActive ? filteredOrders : orders),
    [isFilterActive, filteredOrders, orders]
  );

  const getSortValue = (order, key) => {
    if (!order || typeof order !== "object") {
      return "";
    }

    switch (key) {
      case "tipoParte":
        return tipoParteLabels[order.TipoParte] || order.TipoParte || "";
      case "tecnico":
        return order.USER_NAME || "";
      //return resolveTechnicianName(order) || "";
      case "grupoTecnico":
        return order.USER_GROUP_NAME || "";
      //return resolveGroupName(order) || "";
      case "estado": {
        const numericState = Number(order.Estado);
        return Number.isNaN(numericState)
          ? estadoLabels[order.Estado] || order.Estado || ""
          : numericState;
      }
      case "reportado":
        return isReportado(order.Reportado) ? 1 : 0;
      case "cups":
        return order.CUPS || "";
      case "cntId":
        return order.CntId || "";
      case "actualizado": {
        if (!order.Modificado) return 0;
        const timestamp = new Date(order.Modificado).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      case "coordX":
        return Number(order.COORDENADA_X) || 0;
      case "coordY":
        return Number(order.COORDENADA_Y) || 0;
      case "nombre":
        return order.NOMBRECT || "";
      case "direccion":
        return order.DIRECCIONSUMINISTRO || "";
      default:
        return "";
    }
  };

  const sortedOrders = useMemo(() => {
    if (!Array.isArray(baseOrders)) {
      return [];
    }

    if (!sortConfig.key) {
      return baseOrders;
    }

    const sorted = [...baseOrders].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      const normalize = (value) =>
        value === undefined || value === null ? "" : value;

      const normalizedA = normalize(aValue);
      const normalizedB = normalize(bValue);

      const bothNumeric =
        typeof normalizedA === "number" && typeof normalizedB === "number";

      if (bothNumeric) {
        return sortConfig.direction === "asc"
          ? normalizedA - normalizedB
          : normalizedB - normalizedA;
      }

      const aString = String(normalizedA);
      const bString = String(normalizedB);

      return sortConfig.direction === "asc"
        ? aString.localeCompare(bString, "es", {
            numeric: true,
            sensitivity: "base",
          })
        : bString.localeCompare(aString, "es", {
            numeric: true,
            sensitivity: "base",
          });
    });

    return sorted;
  }, [baseOrders, sortConfig, technicianNames, groupNames, getSortValue]);

  const ordersWithCoordinates = useMemo(() => {
    return sortedOrders.filter((order) => {
      if (!order) {
        return false;
      }

      const latitudeRaw = order.COORDENADA_X;
      const longitudeRaw = order.COORDENADA_Y;

      if (
        latitudeRaw === undefined ||
        latitudeRaw === null ||
        longitudeRaw === undefined ||
        longitudeRaw === null
      ) {
        return false;
      }

      const latitude = Number(String(latitudeRaw).replace(/,/g, ".").trim());
      const longitude = Number(String(longitudeRaw).replace(/,/g, ".").trim());

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
      }

      if (latitude === 0 && longitude === 0) {
        return false;
      }

      return true;
    });
  }, [sortedOrders]);

  useEffect(() => {
    setVisibleCount((current) => {
      const initial = Math.min(INITIAL_BATCH_SIZE, sortedOrders.length);
      if (current === initial) {
        return current;
      }
      return initial;
    });
  }, [sortedOrders]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;

    if (!sentinel || !container) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting) {
          setVisibleCount((prev) => {
            if (prev >= sortedOrders.length) {
              return prev;
            }
            return Math.min(prev + BATCH_INCREMENT, sortedOrders.length);
          });
        }
      },
      {
        root: container,
        threshold: 1,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sortedOrders.length]);

  const visibleOrders = useMemo(
    () => sortedOrders.slice(0, visibleCount),
    [sortedOrders, visibleCount]
  );

  const hasMoreOrders = visibleCount < sortedOrders.length;

  const applyColumnFilters = () => {
    if (!orders || orders.length === 0) {
      setFilteredOrders([]);
      return;
    }

    const filtered = orders.filter((order) => {
      const matches = {
        tipoParte:
          !columnFilters.tipoParte ||
          (tipoParteLabels[order.TipoParte] || "")
            .toLowerCase()
            .includes(columnFilters.tipoParte.toLowerCase()),
        /*
      tecnico:
        !columnFilters.tecnico ||
        resolveTechnicianName(order)
          .toLowerCase()
          .includes(columnFilters.tecnico.toLowerCase()),

      grupoTecnico:
        !columnFilters.grupoTecnico ||
        resolveGroupName(order)
          .toLowerCase()
          .includes(columnFilters.grupoTecnico.toLowerCase()),
*/
        tecnico:
          !columnFilters.tecnico ||
          (order.USER_NAME || "")
            .toLowerCase()
            .includes(columnFilters.tecnico.toLowerCase()),

        grupoTecnico:
          !columnFilters.grupoTecnico ||
          (order.USER_GROUP_NAME || "")
            .toLowerCase()
            .includes(columnFilters.grupoTecnico.toLowerCase()),

        estado:
          !columnFilters.estado ||
          (estadoLabels[order.Estado] || "")
            .toLowerCase()
            .includes(columnFilters.estado.toLowerCase()),
        reportado:
          !columnFilters.reportado ||
          (columnFilters.reportado === "si"
            ? isReportado(order.Reportado)
            : !isReportado(order.Reportado)),
        cups:
          !columnFilters.cups ||
          (order.CUPS || "")
            .toLowerCase()
            .includes(columnFilters.cups.toLowerCase()),

        cntId:
          !columnFilters.cntId ||
          (order.CntId || "")
            .toLowerCase()
            .includes(columnFilters.cntId.toLowerCase()),

        actualizado:
          !columnFilters.actualizado ||
          formatDateTime(order.Modificado)
            .toLowerCase()
            .includes(columnFilters.actualizado.toLowerCase()),

        coordX:
          !columnFilters.coordX ||
          String(order.COORDENADA_X || "")
            .toLowerCase()
            .includes(columnFilters.coordX.toLowerCase()),

        coordY:
          !columnFilters.coordY ||
          String(order.COORDENADA_Y || "")
            .toLowerCase()
            .includes(columnFilters.coordY.toLowerCase()),

        nombre:
          !columnFilters.nombre ||
          (order.NOMBRECT || "")
            .toLowerCase()
            .includes(columnFilters.nombre.toLowerCase()),

        direccion:
          !columnFilters.direccion ||
          (order.DIRECCIONSUMINISTRO || "")
            .toLowerCase()
            .includes(columnFilters.direccion.toLowerCase()),
      };

      // L'ordine passa se tutti i filtri corrispondono
      return Object.values(matches).every(Boolean);
    });

    setFilteredOrders(filtered);
    setIsFilterActive(
      Object.values(columnFilters).some((v) => v.trim() !== "")
    );
  };

  const handleSaveAssignment = async (assignmentData = {}) => {
    const targetClient =
      assignmentData.clientCode ||
      clientCodId ||
      (selectedOrder ? resolveOrderClientCode(selectedOrder) : "");

    if (!targetClient) {
      toast.error("No se pudo determinar el cliente del parte");
      return false;
    }

    try {
      await loadOrdersForClient(targetClient);
      return true;
    } catch (err) {
      console.error("Error al recargar órdenes tras la reasignación:", err);
      toast.error(
        "No se pudieron recargar las órdenes después de la reasignación"
      );
      return false;
    }
  };

  const handleShowOrders = async () => {
    if (!clientCodId) {
      toast.error("Selecciona un cliente primero");
      setShowClientModal(true);
      return;
    }

    await loadOrdersForClient(clientCodId);
  };

  const handleAction = (action, order) => {
    setSelectedOrder(order);

    switch (action) {
      case "modificar":
        setShowAssignmentModal(true);
        break;
      case "eliminar":
        setShowDeleteModal(true);
        break;
      case "reporte":
        setShowReportModal(true);
        break;
      default:
        break;
    }
  };

  const handleToggle = (order) => {
    const exists = selectedOrders.find((o) => o.COD_ID === order.COD_ID);

    if (exists) {
      // Si ya está seleccionado → deselecciona
      setSelectedOrders((prev) =>
        prev.filter((o) => o.COD_ID !== order.COD_ID)
      );
    } else {
      // Si no está → agrégalo
      setSelectedOrders((prev) => [...prev, order]);
    }
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return { key: columnKey, direction: "asc" };
    });
  };

  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-indicator">↕</span>;
    }

    return (
      <span className="sort-indicator">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const stateActionsMap = {
    1: ["modificar", "eliminar", "verMapa"],
    2: ["verMapa"],
    3: ["verMapa"],
    4: ["verMapa", "reporte"],
    5: ["verMapa", "reporte"],
  };

  const isActionEnabledForOrder = (action, order) => {
    const estado = order?.Estado ? String(order.Estado) : "";
    const allowed = stateActionsMap[estado] || ["verMapa"];

    if (action === "reporte" && DEBUG_ENABLE_REPORT) {
      return true;
    }

    return allowed.includes(action);
  };

  const handleAssignmentClick = (order) => {
    if (!canReassign || !isActionEnabledForOrder("modificar", order)) {
      return;
    }

    handleAction("modificar", order);
  };

  const handleDeleteClick = (order) => {
    if (!isActionEnabledForOrder("eliminar", order)) {
      return;
    }

    handleAction("eliminar", order);
  };

  const handleOpenMapModal = (order = null) => {
    // Si se pasa un orden individual -> se muestra solo esa
    if (order) {
      setOrdersToShowInMap([order]);
      setMapFocusedOrderId(String(order.COD_ID));
    }
    // Si hay varias seleccionadas -> mostrar solo esas
    else if (selectedOrders.length > 0) {
      const withCoords = selectedOrders.filter(
        (o) => o.COORDENADA_X && o.COORDENADA_Y
      );

      if (withCoords.length === 0) {
        toast.error("Las órdenes seleccionadas no tienen coordenadas válidas.");
        return;
      }

      setOrdersToShowInMap(withCoords);
      setMapFocusedOrderId(null);
    }
    // Si no hay selección -> mostrar todas
    else {
      setOrdersToShowInMap(ordersWithCoordinates);
      setMapFocusedOrderId(null);
    }

    setShowMapModal(true);
  };

  const handleViewOnMap = (order) => {
    if (!isActionEnabledForOrder("verMapa", order)) {
      return;
    }

    if (order) {
      // Apri la mappa SOLO con l'ordine selezionato
      setMapFocusedOrderId(order.COD_ID ? String(order.COD_ID) : null);
      setShowMapModal(true);
      setOrdersToShowInMap([order]); // 👈 nuovo stato
    } else {
      // Caso del bottone in alto -> mostra tutti
      setMapFocusedOrderId(null);
      setShowMapModal(true);
      setOrdersToShowInMap(sortedOrders);
    }
  };

  const handleReportClick = (order) => {
    if (!isActionEnabledForOrder("reporte", order)) {
      return;
    }

    handleAction("reporte", order);
  };

  const handleSaveOrder = async (codId, estado, comments) => {
    try {
      const updateData = {
        COD_ID: codId,
        Estado: estado,
      };

      if (comments.trim()) {
        updateData.Comments = [comments];
      }

      if (estado === "4") {
        updateData.Terminado = new Date().toISOString();
      }

      await updateOrders([updateData]);

      // Ricarica ordini
      await loadOrdersForClient(clientCodId);

      toast.success("Parte actualizado correctamente");
      return true;
    } catch (err) {
      console.error("Error al actualizar parte:", err);
      toast.error("Error al actualizar el parte");
      return false;
    }
  };

  const handleDeleteOrder = async () => {
    try {
      await deleteOrder(selectedOrder.COD_ID, clientCodId);

      // Ricarica ordini
      await loadOrdersForClient(clientCodId);

      toast.success("Parte eliminado correctamente");
      return true;
    } catch (err) {
      console.error("Mensaje de la API:", err);
      toast.error("Mensaje de la API: " + err);
      return false;
    }
  };

  const handleOpenBulkDeleteModal = () => {
  if (selectedOrders.length === 0) {
    toast.error("Selecciona al menos un parte para eliminar");
    return;
  }
  setShowBulkDeleteModal(true);
};

  const handleBulkDeleteOrders = async () => {
    try {
      // Filter only orders with Estado = 1 (Nuevo)
      const ordersToDelete = selectedOrders.filter(order => String(order.Estado) === "1");
      
      if (ordersToDelete.length === 0) {
        toast.error("No hay partes con estado 'Nuevo' en la selección");
        setShowBulkDeleteModal(false);
        return false;
      }

      const skippedOrders = selectedOrders.length - ordersToDelete.length;
      
      // Show info if some orders will be skipped
      if (skippedOrders > 0) {
        toast.info(`Se omitirán ${skippedOrders} parte(s) que no están en estado 'Nuevo'`);
      }

      const results = {
        success: [],
        failed: []
      };

      // Delete each order individually
      for (const order of ordersToDelete) {
        try {
          await deleteOrder(order.COD_ID, clientCodId);
          results.success.push(order);
        } catch (err) {
          console.error(`Error al eliminar parte ${order.COD_ID}:`, err);
          results.failed.push({
            order,
            error: err.message || err
          });
        }
      }

      // Reload orders
      await loadOrdersForClient(clientCodId);

      // Clear selection
      setSelectedOrders([]);

      // Show final results
      if (results.failed.length === 0) {
        toast.success(`${results.success.length} parte(s) eliminado(s) correctamente`);
      } else if (results.success.length === 0) {
        toast.error(`No se pudo eliminar ningún parte`);
      } else {
        toast.warning(
          `${results.success.length} parte(s) eliminado(s). ` +
          `${results.failed.length} parte(s) no se pudieron eliminar`
        );
        
        // Show details of failed deletions
        const failedDetails = results.failed
          .map(f => `CUPS: ${f.order.CUPS}`)
          .join(", ");
        
        console.error("Partes no eliminados:", failedDetails);
        toast.error(`Partes no eliminados: ${failedDetails}`);
      }

      return true;
    } catch (err) {
      console.error("Error en eliminación masiva:", err);
      toast.error("Error al eliminar partes");
      return false;
    }
  };
  
const handleBulkGenerateReports = async () => {
  if (selectedOrders.length === 0) {
    toast.error("Selecciona al menos un parte para generar reporte");
    return;
  }

  try {
    // Filtrar solo órdenes con Estado = 4 (Finalizado) o 5 (Reportado)
    const ordersToReport = selectedOrders.filter(
      order => String(order.Estado) === "4" || String(order.Estado) === "5"
    );
    
    if (ordersToReport.length === 0) {
      toast.error("No hay partes con estado 'Finalizado' o 'Reportado' en la selección");
      return;
    }

    const skippedOrders = selectedOrders.length - ordersToReport.length;
    
    // Mostrar info si algunas órdenes fueron omitidas
    if (skippedOrders > 0) {
      toast.info(
        `Se omitirán ${skippedOrders} parte(s) que no están en estado 'Finalizado' o 'Reportado'`
      );
    }

    const results = {
      success: [],
      failed: []
    };

    // ✅ FIX: Disable pointer events to prevent UI interactions during downloads
    const originalPointerEvents = document.body.style.pointerEvents;
    document.body.style.pointerEvents = 'none';

    try {
      // Generar y descargar cada reporte individualmente
      for (const order of ordersToReport) {
        try {
          // Llamar a la API para obtener el reporte
          const data = await getOrderReport(order.COD_ID);
                  
          if (data && data.length > 0 && data[0].ReportData) {
            const reportData = data[0].ReportData;
            const reportContent = reportData.join('\n');
            
            // ✅ FIX: Create download without triggering page refresh
            const blob = new Blob([reportContent], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `reporte_parte_${order.COD_ID}_${order.CUPS}.txt`;
            
            // ✅ FIX: Set attributes to prevent navigation
            a.style.display = 'none';
            a.setAttribute('target', '_self');
            
            // Append, click, and remove in a controlled way
            document.body.appendChild(a);
            
            // ✅ FIX: Use setTimeout to ensure proper download initiation
            await new Promise((resolve) => {
              setTimeout(() => {
                a.click();
                resolve();
              }, 100);
            });
            
            // Clean up immediately after click
            document.body.removeChild(a);
            
            // ✅ FIX: Delay URL revocation to ensure download starts
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 1000);
            
            results.success.push(order);
            
            // ✅ FIX: Increased pause between downloads for stability
            await new Promise(resolve => setTimeout(resolve, 500));
          } else {
            results.failed.push({
              order,
              error: "El reporte no contiene datos"
            });
          }
        } catch (err) {
          console.error(`Error al generar reporte para parte ${order.COD_ID}:`, err);
          results.failed.push({
            order,
            error: err.message || err
          });
        }
      }
    } finally {
      // ✅ FIX: Always restore pointer events after downloads complete
      document.body.style.pointerEvents = originalPointerEvents;
    }

    // Limpiar selección
    setSelectedOrders([]);

    // Mostrar resultados finales
    if (results.failed.length === 0) {
      toast.success(`${results.success.length} reporte(s) descargado(s) correctamente`);
    } else if (results.success.length === 0) {
      toast.error(`No se pudo generar ningún reporte`);
    } else {
      toast.warning(
        `${results.success.length} reporte(s) descargado(s). ` +
        `${results.failed.length} reporte(s) fallaron`
      );
      
      // Mostrar detalles de los reportes que fallaron
      const failedDetails = results.failed
        .map(f => `COD_ID: ${f.order.COD_ID}, CUPS: ${f.order.CUPS}`)
        .join("; ");
      
      console.error("Reportes fallidos:", failedDetails);
      toast.error(`Reportes fallidos: ${failedDetails}`);
    }

    return true;
  } catch (err) {
    console.error("Error en generación masiva de reportes:", err);
    toast.error("Error al generar reportes");
    return false;
  }
};

  const formatDateTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleOpenCreateOrderModal = async () => {
    if (loadingCreateOrderData) {
      return;
    }

    if (!clientCodId) {
      toast.error("Selecciona un cliente antes de crear un parte");
      return;
    }

    try {
      setLoadingCreateOrderData(true);
      setErpDataForNewOrders([]);

      const erpResponse = await getERPPartes(clientCodId);
      const normalized = Array.isArray(erpResponse)
        ? erpResponse
        : erpResponse
        ? [erpResponse]
        : [];

      if (!normalized.length) {
        toast.error(
          "No se encontraron CUPS disponibles en el ERP para este cliente"
        );
        return;
      }

      setErpDataForNewOrders(normalized);
      setShowCreateOrderModal(true);
    } catch (error) {
      console.error("Error al preparar la creación de partes", error);
      toast.error("Error al cargar los datos del ERP para crear partes");
    } finally {
      setLoadingCreateOrderData(false);
    }
  };

  const handleCloseCreateOrderModal = () => {
    setShowCreateOrderModal(false);
    setErpDataForNewOrders([]);
  };

  const handleCreateOrders = async (ordersPayload) => {
    try {
      const result = await createOrders(ordersPayload);
      const statusValue = result?.status;
      const numericStatus = Number(statusValue);

      if (!Number.isNaN(numericStatus) && numericStatus > 0) {
        toast.success(`${numericStatus} parte(s) creados correctamente`);
        await loadOrdersForClient(clientCodId);
        return true;
      }

      if (
        typeof statusValue === "string" &&
        statusValue.toUpperCase() === "OK"
      ) {
        toast.success("Partes creados correctamente");
        await loadOrdersForClient(clientCodId);
        return true;
      }

      toast.error("No se pudieron crear los partes");
      return false;
    } catch (error) {
      console.error("Error al crear partes", error);
      toast.error(`Error al crear partes: ${error.message || error}`);
      return false;
    }
  };

  return (
    <Layout>
      {clientCodId && (
        <div className="dashboard">
           <div className="ventana ventana--workorders mostrar">
            <span className="ventana_tittle">Partes de Trabajo</span>

            <div className="ventana_menu">
              <div className="menu_descripcion">
                <span>
                  <b>Cliente:</b>{" "}
                  {clientName || selectedClient || "Selecciona un cliente"}
                </span>
                <span>
                  <b>Usuarios</b>
                </span>
              </div>
              <div className="menu_opciones">
                <span
                  className={`ficha ${
                    (availableClients?.length || 0) === 0 ? "disable" : ""
                  }`}
                  onClick={() => {
                    if ((availableClients?.length || 0) === 0) return;
                    setShowClientModal(true);
                    setSelectedClient(clientName || selectedClient || "");
                  }}
                >
                  Cambiar cliente
                </span>
                <span className="ficha" onClick={handleShowOrders}>
                  Mostrar partes
                </span>
                <span 
                  className={`ficha ${selectedOrders.length === 0 ? "disable" : ""}`}
                  onClick={handleOpenBulkDeleteModal}
                >
                  Eliminar Parte
                </span>
                <span 
                  className={`ficha ${selectedOrders.length === 0 ? "disable" : ""}`}
                  onClick={handleBulkGenerateReports}
                >
                  Generar reporte
                </span>
                <span className="ficha" onClick={() => handleOpenMapModal(null)}>
                  Ver en mapa
                </span>
                {/*} <span
                  className={`ficha${!clientCodId || loadingCreateOrderData ? " disable" : ""}`}
                  onClick={handleOpenCreateOrderModal}
                >
                  Crear nuevo parte
                </span>*/}
                <span
                  className="ficha"
                  onClick={() => navigate("/create-work-order")}
                >
                  Crear nuevo parte
                </span>
              </div>
            </div>
            {showTable && (
              <div className="ventana_resultado_visible ventana_resultado_visible--workorders">
                {loading ? (
                  <p>Cargando...</p>
                ) : (
                  <div className="tabla tabla--workorders" ref={scrollContainerRef}  style={{
                      overflowY: "auto",
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                    }}>
                    <table className="workorders-table">
                      <thead>
                        <tr className="filters-row">
                          <th style={{ width: 30 }}>
                            <input
                              title={
                                selectedOrders.length == orders.length
                                  ? "Deseleccionar todos"
                                  : "Seleccionar todos"
                              }
                              style={{ marginLeft: "-5px" }}
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Selecciona todos los visibles
                                  setSelectedOrders(visibleOrders);
                                } else {
                                  // Deselecciona todos
                                  setSelectedOrders([]);
                                }
                              }}
                              checked={
                                selectedOrders.length > 0 &&
                                selectedOrders.length === visibleOrders.length
                              }
                              indeterminate={
                                selectedOrders.length > 0 &&
                                selectedOrders.length < visibleOrders.length
                              }
                            />
                          </th>
                          <th style={columnStyleMap.tipoParte}>
                            <input
                              type="text"
                              value={columnFilters.tipoParte}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  tipoParte: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "tipoParte")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.tecnico}>
                            <input
                              type="text"
                              value={columnFilters.tecnico}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  tecnico: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "tecnico")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.grupoTecnico}>
                            <input
                              type="text"
                              value={columnFilters.grupoTecnico}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  grupoTecnico: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "grupoTecnico")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.estado}>
                            <select
                              value={columnFilters.estado}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  estado: e.target.value,
                                })
                              }
                            >
                              <option value="">Todos</option>
                              {Object.entries(estadoLabels)
                                .filter(([key]) => !["0", "5"].includes(key))
                                .map(([, label]) => (
                                  <option key={label} value={label.toLowerCase()}>
                                    {label}
                                  </option>
                                ))}
                            </select>
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "estado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.reportado}>
                            <select
                              value={columnFilters.reportado}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  reportado: e.target.value,
                                })
                              }
                            >
                              <option value="">Todos</option>
                              <option value="si">Sí</option>
                              <option value="no">No</option>
                            </select>
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "reportado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.cups}>
                            <input
                              type="text"
                              value={columnFilters.cups}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  cups: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "cups")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.cntId}>
                            <input
                              type="text"
                              value={columnFilters.cntId}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  cntId: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "cntId")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.actualizado}>
                            <input
                              type="text"
                              value={columnFilters.actualizado}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  actualizado: e.target.value,
                                })
                              }
                              placeholder="Fecha..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "actualizado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.coordX}>
                            <input
                              type="text"
                              value={columnFilters.coordX}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  coordX: e.target.value,
                                })
                              }
                              placeholder="X"
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "coordX")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.coordY}>
                            <input
                              type="text"
                              value={columnFilters.coordY}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  coordY: e.target.value,
                                })
                              }
                              placeholder="Y"
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "coordY")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.nombre}>
                            <input
                              type="text"
                              value={columnFilters.nombre}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  nombre: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "nombre")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.direccion}>
                            <input
                              type="text"
                              value={columnFilters.direccion}
                              onChange={(e) =>
                                setColumnFilters({
                                  ...columnFilters,
                                  direccion: e.target.value,
                                })
                              }
                              placeholder="Filtrar..."
                            />
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "direccion")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={{
                              ...columnStyleMap.acciones,
                              textAlign: "right",
                            }}
                          >
                            <button
                              onClick={() =>
                                setColumnFilters({
                                  tipoParte: "",
                                  tecnico: "",
                                  grupoTecnico: "",
                                  estado: "",
                                  reportado: "",
                                  cups: "",
                                  cntId: "",
                                  actualizado: "",
                                  coordX: "",
                                  coordY: "",
                                  nombre: "",
                                  direccion: "",
                                })
                              }
                              className="btn-limpiar-filtros"
                            >
                              Borrar filtros
                            </button>
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "acciones")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                        </tr>

                        <tr>
                          <th></th>
                          <th
                            style={columnStyleMap.tipoParte}
                            onClick={() => handleSort("tipoParte")}
                            aria-sort={
                              sortConfig.key === "tipoParte"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Tipo de parte {renderSortIndicator("tipoParte")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "tipoParte")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.tecnico}
                            onClick={() => handleSort("tecnico")}
                            aria-sort={
                              sortConfig.key === "tecnico"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Técnico {renderSortIndicator("tecnico")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "tecnico")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.grupoTecnico}
                            onClick={() => handleSort("grupoTecnico")}
                            aria-sort={
                              sortConfig.key === "grupoTecnico"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Grupo técnico {renderSortIndicator("grupoTecnico")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "grupoTecnico")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.estado}
                            onClick={() => handleSort("estado")}
                            aria-sort={
                              sortConfig.key === "estado"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Estado {renderSortIndicator("estado")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "estado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.reportado}
                            onClick={() => handleSort("reportado")}
                            aria-sort={
                              sortConfig.key === "reportado"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Reportado {renderSortIndicator("reportado")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "reportado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.cups}
                            onClick={() => handleSort("cups")}
                            aria-sort={
                              sortConfig.key === "cups"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            CUPS {renderSortIndicator("cups")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "cups")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={{
                              ...columnStyleMap.cntId,
                              paddingLeft: "40px",
                            }}
                            onClick={() => handleSort("cntId")}
                            aria-sort={
                              sortConfig.key === "cntId"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            CntId {renderSortIndicator("cntId")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "cntId")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.actualizado}
                            onClick={() => handleSort("actualizado")}
                            aria-sort={
                              sortConfig.key === "actualizado"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Actualizado {renderSortIndicator("actualizado")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "actualizado")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.coordX}
                            onClick={() => handleSort("coordX")}
                            aria-sort={
                              sortConfig.key === "coordX"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Coord. X {renderSortIndicator("coordX")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "coordX")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.coordY}
                            onClick={() => handleSort("coordY")}
                            aria-sort={
                              sortConfig.key === "coordY"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            Coord. Y {renderSortIndicator("coordY")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "coordY")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.nombre}
                            onClick={() => handleSort("nombre")}
                            aria-sort={
                              sortConfig.key === "nombre"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            NOMBRECT {renderSortIndicator("nombre")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "nombre")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th
                            style={columnStyleMap.direccion}
                            onClick={() => handleSort("direccion")}
                            aria-sort={
                              sortConfig.key === "direccion"
                                ? sortConfig.direction
                                : "none"
                            }
                          >
                            DIRECCIONSUMINISTRO {renderSortIndicator("direccion")}
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "direccion")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                          <th style={columnStyleMap.acciones}>
                            Acciones
                            <div
                              className="column-resizer"
                              onMouseDown={(event) =>
                                handleColumnResizeMouseDown(event, "acciones")
                              }
                              onClick={handleResizeHandleClick}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleOrders.length === 0 ? (
                          <tr>
                            <td colSpan={14} style={{ textAlign: "center" }}>
                              No se encontraron partes para los filtros aplicados.
                            </td>
                          </tr>
                        ) : (
                          visibleOrders.map((o) => {
                            const isSelected = selectedOrders.some(
                              (so) => so.COD_ID === o.COD_ID
                            );

                            return (
                              <tr
                                key={o.COD_ID}
                                onClick={() => handleToggle(o)}
                                className={isSelected ? "selected-row" : ""}
                                style={{ cursor: "pointer" }}
                              >
                                {/* 🔹 Nueva celda con checkbox */}
                                <td style={{ width: 30 }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggle(o)}
                                    onClick={(e) => e.stopPropagation()} // evita el doble evento
                                  />
                                </td>

                                {/* Resto de columnas existentes */}
                                <td style={columnStyleMap.tipoParte}>
                                  {tipoParteLabels[o.TipoParte] || o.TipoParte}
                                </td>

                                <td style={columnStyleMap.tecnico}>
                                  {o.USER_NAME || ""}
                                </td>
                                <td style={columnStyleMap.grupoTecnico}>
                                  {o.USER_GROUP_NAME || ""}
                                </td>
                                <td style={columnStyleMap.estado}>
                                  {estadoLabels[o.Estado] || o.Estado}
                                </td>
                                <td style={columnStyleMap.reportado}>
                                  {formatReportado(o.Reportado)}
                                </td>
                                <td style={columnStyleMap.cups} title={o.CUPS}>
                                  {o.CUPS}
                                </td>
                                <td style={columnStyleMap.cntId} title={o.CntId}>
                                  {o.CntId}
                                </td>
                                <td style={columnStyleMap.actualizado}>
                                  {formatDateTime(o.Modificado)}
                                </td>
                                <td
                                  style={columnStyleMap.coordX}
                                  title={o.COORDENADA_X ?? ""}
                                >
                                  {o.COORDENADA_X}
                                </td>
                                <td
                                  style={columnStyleMap.coordY}
                                  title={o.COORDENADA_Y ?? ""}
                                >
                                  {o.COORDENADA_Y}
                                </td>
                                <td style={columnStyleMap.nombre}>
                                  {o.NOMBRECT}
                                </td>
                                <td style={columnStyleMap.direccion}>
                                  {o.DIRECCIONSUMINISTRO}
                                </td>

                                {/* Acciones existentes */}
                                <td style={columnStyleMap.acciones}>
                                  <i className="bi bi-three-dots-vertical">
                                    <div className="acciones">
                                      <span
                                        className={`item_acciones ${
                                          isActionEnabledForOrder("modificar", o)
                                            ? ""
                                            : "disable"
                                        }`}
                                        onClick={() => handleAssignmentClick(o)}
                                      >
                                        Modificar Parte
                                      </span>
                                      <span
                                        className={`item_acciones ${
                                          isActionEnabledForOrder("eliminar", o)
                                            ? ""
                                            : "disable"
                                        }`}
                                        onClick={() => handleDeleteClick(o)}
                                      >
                                        Eliminar Parte
                                      </span>
                                      <span
                                        className={`item_acciones ${
                                          isActionEnabledForOrder("verMapa", o)
                                            ? ""
                                            : "disable"
                                        }`}
                                        onClick={() => handleViewOnMap(o)}
                                      >
                                        Ver en mapa
                                      </span>
                                      <span
                                        className={`item_acciones ${
                                          isActionEnabledForOrder("reporte", o)
                                            ? ""
                                            : "disable"
                                        }`}
                                        onClick={() => handleReportClick(o)}
                                      >
                                        Generar reporte
                                      </span>
                                    </div>
                                  </i>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                    <div
                      ref={sentinelRef}
                      style={{ height: "10px", width: "100%" }}
                    />
                    {hasMoreOrders && visibleOrders.length > 0 && (
                      <div className="mensaje-carga">
                        Cargando más resultados…
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {showDeleteModal && selectedOrder && (
        <ModalDeleteOrder
          order={selectedOrder}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteOrder}
        />
      )}
      {showBulkDeleteModal && (
        <ModalBulkDeleteOrder
          selectedCount={selectedOrders.length}
          onClose={() => setShowBulkDeleteModal(false)}
          onConfirm={handleBulkDeleteOrders}
        />
      )}
      {showReportModal && selectedOrder && (
        <ModalViewReport
          order={selectedOrder}
          onClose={() => setShowReportModal(false)}
        />
      )}
      {showAssignmentModal && selectedOrder && (
        <ModalEditAssignment
          order={selectedOrder}
          clientCode={clientCodId}
          onClose={() => setShowAssignmentModal(false)}
          onSave={handleSaveAssignment}
        />
      )}
      {showClientModal && (
        <ModalSelectClient
          clients={availableClients}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          onSelect={handleSelectClient}
          onClose={handleCloseClientModal}
        />
      )}
      {showMapModal && (
        <ModalMapOrders
          isOpen={showMapModal}
          orders={ordersToShowInMap} // 👈 invece di sortedOrders
          focusedOrderId={mapFocusedOrderId}
          onClose={() => {
            setShowMapModal(false);
            setMapFocusedOrderId(null);
            setSelectedOrders([]);
          }}
          estadoLabels={estadoLabels}
          tipoParteLabels={tipoParteLabels}
          // resolveTechnicianName={resolveTechnicianName}
          // resolveGroupName={resolveGroupName}
          clientName={clientName || selectedClient}
        />
      )}
      {showCreateOrderModal && (
        <ModalCreateOrder
          erpData={erpDataForNewOrders}
          clientCodId={clientCodId}
          onClose={handleCloseCreateOrderModal}
          onSave={handleCreateOrders}
        />
      )}
    </Layout>
  );
}

export default WorkOrder;
